import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';

/* ============================================================================
   /login
   ----------------------------------------------------------------------------
   One route, two steps. A password on an account with two-factor on buys a
   five-minute token and nothing else, so this screen swaps itself for a code
   field rather than navigating: there is no session yet, so there is nothing
   to protect a second route with, and a URL for the half-finished state is a
   URL somebody can bookmark and return to with a token that expired an hour
   ago.
   ========================================================================== */

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const { status, login, expired } = useSession();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** Non-null once the password is accepted and a second factor is wanted. */
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  if (status === 'authenticated') {
    const from = (location.state as LocationState | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const outcome = await login(email, password);
      if (outcome.mfaRequired) {
        setMfaToken(outcome.mfaToken);
        /* The password has done its job and is not needed again. Holding it
           in component state through the second step would keep it in memory
           — and in any error overlay that serialises props — for no reason. */
        setPassword('');
      }
    } catch (caught) {
      /* The API returns one message for every cause on purpose — unknown
         email, wrong password, disabled account. Show exactly what it said
         rather than guessing at something more helpful, which would undo the
         reason it is vague. */
      setError(caught instanceof ApiError ? caught.message : 'Could not log in.');
    } finally {
      setBusy(false);
    }
  }

  if (mfaToken) {
    return (
      <SecondFactorStep
        token={mfaToken}
        onStartOver={() => {
          setMfaToken(null);
          setError('');
        }}
      />
    );
  }

  return (
    <div className="centre">
      <div className="centre__box card stack">
        <h1>Admin login</h1>

        {/* Said before they wonder. A login form that appears unannounced
            halfway through an afternoon reads as something broken. */}
        {expired && !error ? (
          <p className="alert" role="status">
            You were signed out because nothing happened for a while. Log in to carry on.
          </p>
        ) : null}

        <form className="stack" onSubmit={(e) => void onSubmit(e)} noValidate>
          {error ? (
            <p className="alert" role="alert">
              {error}
            </p>
          ) : null}

          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <button className="btn" type="submit" disabled={busy}>
            {busy ? 'Logging in…' : 'Log in'}
          </button>
        </form>

        <Link to="/forgot-password" className="muted">
          Forgot your password?
        </Link>
      </div>
    </div>
  );
}

/* --- the second step ------------------------------------------------------- */

function SecondFactorStep({ token, onStartOver }: { token: string; onStartOver: () => void }) {
  const { completeTwoFactor } = useSession();
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await completeTwoFactor(token, useRecovery ? { recoveryCode } : { code });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not finish logging in.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="centre">
      <div className="centre__box card stack">
        <h1>Two-factor code</h1>
        <p className="muted">
          {useRecovery
            ? 'Enter one of the recovery codes you saved when you turned this on.'
            : 'Open your authenticator app and enter the six-digit code for makeyoursite.in.'}
        </p>

        <form className="stack" onSubmit={(e) => void onSubmit(e)} noValidate>
          {error ? (
            <p className="alert" role="alert">
              {error}
            </p>
          ) : null}

          {useRecovery ? (
            <div className="field">
              <label htmlFor="recoveryCode">Recovery code</label>
              <input
                id="recoveryCode"
                name="recoveryCode"
                type="text"
                /* Not `one-time-code`: that invites the browser and the OS to
                   offer an SMS or authenticator value, which is the wrong
                   thing entirely here. */
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                required
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value)}
              />
            </div>
          ) : (
            <div className="field">
              <label htmlFor="code">Six-digit code</label>
              <input
                id="code"
                name="code"
                /* `text` with a numeric inputMode, not `type="number"`: a
                   number input strips leading zeros, and a TOTP code beginning
                   with one is perfectly ordinary. */
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                /* iOS and Android both read this and offer the code straight
                   from the notification. */
                pattern="[0-9 ]*"
                maxLength={7}
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
          )}

          <button className="btn" type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>

        {/* `btn btn--ghost btn--small`, and no new class. Every colour
            combination on this dashboard has to be a row in
            apps/web/scripts/contrast-audit.mjs, which is hand-written and
            discovers nothing: a new variant here would be a pairing nothing
            checks, which is how the FAQ's hover sat below AA for weeks.
            CLAUDE.md. */}
        <div className="row">
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => {
              setUseRecovery((on) => !on);
              setError('');
            }}
          >
            {useRecovery ? 'Use the app instead' : 'Lost your phone?'}
          </button>
          <button type="button" className="btn btn--ghost btn--small" onClick={onStartOver}>
            Start over
          </button>
        </div>
      </div>
    </div>
  );
}
